// src/utils/widgetUtils.ts

// ✅ Define strong types
export interface NavItem {
  id: string;
  label: string;
  url?: string;
  isEnabled?: boolean;
  isNav?: boolean; // Added isNav property for navigation filtering
}

export interface WidgetData {
  items?: NavItem[] | any[]; // Allow flexible item types
  [key: string]: unknown;
}

export interface Widget {
  type: string;
  data?: WidgetData | any; // Allow flexible data structure
  [key: string]: unknown;
}

export interface FilteredWidgets {
  navWidgets: Widget[];
  homeWidgets: Widget[];
}

/**
 * Extracts navigation widgets and home page widgets.
 * - Removes "Our" / "The" from nav labels.
 * - Excludes nav widgets from the home list.
 * - Matches widgets by normalized nav labels.
 */
export const getDynamicHomeWidgets = (allWidgets: Widget[]): FilteredWidgets => {
  if (!Array.isArray(allWidgets)) {
    return { navWidgets: [], homeWidgets: [] };
  }

  // ✅ Separate nav widgets
  const navWidgets = allWidgets.filter((w) =>
    w.type?.toLowerCase().includes("nav")
  );

  // ✅ Extract labels from all nav widgets dynamically
  const navLabels: string[] = navWidgets
    .flatMap((navWidget) => 
      navWidget.data?.items?.map((item: any) =>
        item.label
          ?.toLowerCase()
          .replace(/^(our|the)\s+/i, "") // strip "Our"/"The"
          .replace(/\s+/g, "") // remove spaces
          .trim()
      ) ?? []
    )
    .filter(Boolean);

  // ✅ Filter out nav + nav-related widgets
  const homeWidgets = allWidgets.filter((widget) => {
    const widgetType = widget.type?.toLowerCase() || "";

    // Always exclude nav widgets
    if (widgetType.includes("nav")) {
      return false;
    }

    // Always exclude footer widgets
    if (widgetType.includes("footer")) {
      return false;
    }

    // Check if widget type matches any nav label (these should be filtered out)
    const isNavRelated = navLabels.some((label) => {
      const matches = label.length > 2 && widgetType.includes(label);
      return matches;
    });

    const shouldInclude = !isNavRelated;
    return shouldInclude;
  });

  return { navWidgets, homeWidgets };
};

/**
 * Validates widget structure
 */
export const isValidWidget = (widget: unknown): widget is Widget => {
  return (
    typeof widget === 'object' &&
    widget !== null &&
    'type' in widget &&
    typeof (widget as Widget).type === 'string'
  );
};

/**
 * Safely processes widgets array with validation
 */
export const processWidgetsSafely = (widgets: unknown): Widget[] => {
  if (!Array.isArray(widgets)) {
    return [];
  }
  
  return widgets.filter(isValidWidget);
};
